"use client";

import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import {
  useSettingEnableBrandHoverCard,
  useAssignSettingEnableBrandHoverCard,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableBrandHoverCard";
import { Core } from "@faire/slate/foundation/color";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import {
  optimizeImageUrl,
  optimizeImageUrlFillSquare,
} from "@faire/web/common/images/optimizeImages";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Column, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { Popover } from "@faire/web/slate/utils/Popover/Popover";
import { PopoverContent } from "@faire/web/slate/utils/Popover/PopoverContent";
import { cn } from "@faire/web/tools/cn";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { NoTranslate } from "@faire/web/ui/lib/localization";
import React, { useState } from "react";

const IMAGE_WIDTH = 56;
const VERTICAL_IMAGE_HEIGHT = 75;
const OUTER_HORIZONTAL_PADDING = 12;
const INNER_HORIZONTAL_PADDING = 2;

const MIN_IMAGE_COUNT = 2;
const MAX_IMAGE_COUNT = 3;

const varImageWidth: DynamicVariable<"--f-bhc-image-width"> = {
  variable: "--f-bhc-image-width",
  className: "w-(--f-bhc-image-width)",
};

const varVerticalImageHeight: DynamicVariable<"--f-bhc-vertical-image-height"> =
  {
    variable: "--f-bhc-vertical-image-height",
    className: "h-(--f-bhc-vertical-image-height)",
  };

const getTotalProductCount = (catalogSizeText?: string): number => {
  if (!catalogSizeText) {
    return 0;
  }
  const match = catalogSizeText.match(/(\d+)\s*products?/i);
  return match && match[1] ? parseInt(match[1], 10) : 0;
};

export interface BrandHoverCardData {
  catalogSizeText?: string;
  images: Array<IImage>;
  displayVerticalImages?: boolean;
  brandToken?: string;
}

interface BrandHoverCardProps {
  children: React.ReactNode;
  brandHoverData?: BrandHoverCardData;
}

export const BrandHoverCard: React.FC<BrandHoverCardProps> = ({
  children,
  brandHoverData,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHoveringTrigger, setIsHoveringTrigger] = useState(false);
  const [isHoveringContent, setIsHoveringContent] = useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBrandHoverCardEnabled = useSettingEnableBrandHoverCard();
  const assignSetting = useAssignSettingEnableBrandHoverCard();

  const assignmentRef = useDoActionWhenVisible(() => {
    assignSetting();
  });

  React.useEffect(() => {
    if (!isHoveringTrigger && !isHoveringContent) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 100);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsOpen(true);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHoveringTrigger, isHoveringContent]);

  if (!brandHoverData) {
    return <>{children}</>;
  }

  // TODO: FD-289817 - replace with data from new API after it's added in BE
  const totalProductCount = getTotalProductCount(
    brandHoverData.catalogSizeText
  );
  const productImages = brandHoverData.images.slice(0, MAX_IMAGE_COUNT);

  const remainingCount = Math.max(0, totalProductCount - MAX_IMAGE_COUNT);

  const imageUrls = productImages
    .map((image) =>
      brandHoverData.displayVerticalImages
        ? optimizeImageUrl(image, false, {
            width: IMAGE_WIDTH,
            height: VERTICAL_IMAGE_HEIGHT,
          })
        : optimizeImageUrlFillSquare(image, false, IMAGE_WIDTH)
    )
    .filter(isNotUndefined);

  if (imageUrls.length < MIN_IMAGE_COUNT) {
    return <>{children}</>;
  }

  // Width of the popover is the width of the images + the padding.
  // This needs to be set explcitly because the popover implementation can't calculate the width automatically.
  const popoverWidth =
    Math.min(MAX_IMAGE_COUNT, imageUrls.length) * IMAGE_WIDTH +
    OUTER_HORIZONTAL_PADDING * 2 +
    (Math.min(MAX_IMAGE_COUNT, imageUrls.length) - 1) *
      INNER_HORIZONTAL_PADDING;

  return (
    <Popover open={isOpen} placement="bottom-start" width={popoverWidth}>
      <div
        onMouseEnter={() => setIsHoveringTrigger(true)}
        onMouseLeave={() => setIsHoveringTrigger(false)}
        style={{ display: "inline-block" }}
      >
        {children}
      </div>
      <PopoverContent>
        <div ref={assignmentRef}>
          {isBrandHoverCardEnabled ? (
            <div
              data-test-id="brand-hover-card-content"
              onMouseEnter={() => setIsHoveringContent(true)}
              onMouseLeave={() => setIsHoveringContent(false)}
              className="rounded-lg border border-solid border-[#00000014] bg-[white] p-3 shadow-[0px_2px_10px_0px_#00000026]"
            >
              <Column gap={8}>
                <Row gap={INNER_HORIZONTAL_PADDING}>
                  {imageUrls.map((imageUrl, index) => (
                    <ProductImageThumbnail
                      key={index}
                      imageUrl={imageUrl}
                      isVerticalImage={brandHoverData.displayVerticalImages}
                    />
                  ))}
                </Row>

                {remainingCount > 0 && (
                  <NoTranslate>
                    <Typography
                      variant="labelSansRegular"
                      color={Core.text.subdued}
                      style={{ textAlign: "left" }}
                    >
                      {remainingCount} more products
                    </Typography>
                  </NoTranslate>
                )}
              </Column>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ProductImageThumbnail: React.FC<{
  imageUrl: string;
  isVerticalImage?: boolean;
}> = ({ imageUrl, isVerticalImage }) => {
  return (
    <div
      data-test-id="brand-hover-card-product-image"
      className={cn(
        `w-(--f-bhc-image-width) overflow-hidden bg-cover bg-center bg-no-repeat`,
        isVerticalImage
          ? `h-(--f-bhc-vertical-image-height)`
          : `h-(--f-bhc-image-width)`
      )}
      style={{
        backgroundImage: `url(${imageUrl})`,
        ...assignInlineVars({
          [varImageWidth.variable]: `${IMAGE_WIDTH}px`,
          [varVerticalImageHeight.variable]: `${VERTICAL_IMAGE_HEIGHT}px`,
        }),
      }}
    />
  );
};
